import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IUserProducts } from '../models/Products.interfaces';
import { getAllConsumption } from '../services/ConsumptionService';
import { getAllProducts } from '../services/ProductService';
import { getCustomerData } from '../services/CustomerService';
import {
    addConsumptions,
    addDataSharing,
    getAllProductsId,
    getProducts,
} from '../utils/dataTransformation/productConsumption';
import { IConsumption, IProduct, IUser } from '../utils/store/model/store.model';
import { getUser } from '../utils/store/store';
import { usePersonalDataContext } from '../context/personal-data-context/PersonalDataContext';
import { useGlobalContext, IGlobalContext } from '../context/global-context/GlobalContext';

export enum EGlobalRequest {
    PRODUCTS = 'products',
    USER = 'user',
    CONSUMPTIONS = 'consumptions',
}

const useGlobalRequest = (request: EGlobalRequest, forceUpdate?: boolean) => {
    const dispatch = useDispatch();
    const store = useSelector(getUser);
    const { context, setContext } = useGlobalContext();
    const { setPersonalData } = usePersonalDataContext();
    const { bundles, fibers, mobiles, rentings, consumptions } = context as IGlobalContext;
    const [products, setProducts] = useState<{ [key: string]: IProduct[] | IConsumption[] | null }>({
        bundles: [],
        fibers: [],
        mobiles: [],
        rentings: [],
        consumptions: null,
    });
    const [consumption, setConsumption] = useState<IConsumption[] | null>(consumptions);
    const [user, setInfoUser] = useState<IUser>();
    const handleRequestProducts = () => {
        getAllProducts()
            .then(async (response: AxiosResponse) => {
                const { bundles, fibers, mobiles, rentings } = response.data;
                if (!consumptions) {
                    await handleRequestConsumption(response.data)
                        .then((consumptions: IConsumption[]) => {
                            const context = {
                                bundles,
                                fibers,
                                mobiles,
                                rentings,
                                consumptions,
                            };
                            setProducts(context);
                            setContext(context);
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                } else {
                    const context = { bundles, fibers, mobiles, rentings, consumptions };
                    setProducts(context);
                    setContext(context);
                }
            })
            .catch((error: AxiosError) => {
                console.error(error);
                setProducts({ bundles: [], fibers: [], mobiles: [], rentings: [] });
            });
    };

    const handleRequestUser = () => {
        getCustomerData().then((response: AxiosResponse) => {
            localStorage.setItem(window.btoa('customerId'), response.data.id);
            setInfoUser(response.data);
            setPersonalData(response.data);
        });
    };

    const handleRequestConsumption = async (products: IUserProducts): Promise<IConsumption[]> => {
        const ids = getAllProductsId(products);
        const { data } = await getAllConsumption(ids);
        return new Promise((resolve) => {
            if (data) {
                const { _dataTabs } = getProducts(products);
                const sharedData = addDataSharing(data, _dataTabs.flat());
                let consumptions: IConsumption[] = addConsumptions(data, _dataTabs.flat()).map((item: any) => {
                    return {
                        ...item,
                        sharedData,
                    };
                });

                resolve(consumptions);
            }
        });
    };

    const responseHook = () => {
        const requests: { [key: string]: any } = {
            products: [products],
            user: [user],
        };
        return requests[request];
    };

    useEffect(() => {
        if (request === EGlobalRequest.PRODUCTS) {
            [bundles, fibers, mobiles, rentings].every(
                (item: IProduct[]) => item?.length === 0 || typeof item === 'undefined' || item === null,
            )
                ? handleRequestProducts()
                : setProducts({ bundles, fibers, mobiles, rentings, consumptions });
        }
        if (request === EGlobalRequest.USER) {
            store.user && store.user.user && !forceUpdate ? setInfoUser(store.user?.user) : handleRequestUser();
        }
        if (request === EGlobalRequest.CONSUMPTIONS) {
            consumptions?.length ? setConsumption(consumption) : handleRequestUser();
        }
    }, [dispatch]);

    return responseHook();
};

export default useGlobalRequest;
